'use client'

import { TriangleAlert, RotateCw, ShieldX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { isNemoError } from '@syltek/nemo-core'
import { useTranslations } from 'next-intl'

interface ErrorDisplayProps {
  error?: Error
  onRecoverAttempt?: () => void
}

function ErrorDisplay({ error, onRecoverAttempt }: ErrorDisplayProps) {
  const t = useTranslations('errors')

  // When the error implies the user does not have the necessary permissions to access the resource,
  // we display a different error message with more information.
  const isPermissionError = isNemoError(error) && error?.response?.status === 403

  if (isPermissionError) {
    return (
      <div className="p-8">
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-orange-800 bg-orange-900/20 p-6 text-center">
          <ShieldX className="h-8 w-8 text-orange-300" />
          <div>
            <h2 className="text-lg font-semibold text-orange-200">{t('permission_denied.title')}</h2>
            <p className="mt-1 text-sm text-neutral-400">{t('permission_denied.message')}</p>
          </div>
          <Button
            onClick={onRecoverAttempt}
            variant="outline"
            className="mt-2 border-orange-600 text-orange-100 hover:bg-orange-800/50"
          >
            <RotateCw className="mr-2 h-4 w-4" />
            {t('generic.reload')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-red-800 bg-red-900/20 p-6 text-center">
        <TriangleAlert className="h-8 w-8 text-red-300" />
        <div>
          <h2 className="text-lg font-semibold opacity-70">{t('generic.title')}</h2>
          <p className="mt-1 text-sm text-neutral-500">{error?.message || t('generic.message')}</p>
        </div>
        <Button onClick={onRecoverAttempt} variant="destructive" className="mt-2">
          <RotateCw className="mr-2 h-4 w-4" />
          {t('generic.reload')}
        </Button>
      </div>
    </div>
  )
}

export { ErrorDisplay }
